/**
 * The one media shape every collected item shares (`api`'s post `media` rows and
 * a story's own single item, platform design's media contract): what the stored
 * file actually is, never the provider's own POST/REEL/STORY vocabulary.
 */
import { ChevronLeft, ChevronRight, Film, ImageIcon, Layers, Play } from 'lucide-react';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import { Skeleton } from '../../../ui/skeleton';
import { media } from './messages';

export interface MediaItem {
  ordinal: number;
  media_kind: 'image' | 'video';
  source_url: string | null;
  /** True while `source_url` is null because the file has not been downloaded
   *  yet -- distinct from a signed URL that later fails to load. */
  pending: boolean;
  poster_url: string | null;
  duration_seconds: number | null;
}

function durationLabel(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function PlayBadge(props: { durationSeconds: number | null }): ReactNode {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      <span className="flex size-10 items-center justify-center rounded-full bg-black/50 text-white">
        <Play className="size-5 fill-current" />
      </span>
      {props.durationSeconds !== null && (
        <span className="absolute right-1.5 bottom-1.5 rounded-sm bg-black/70 px-1.5 py-0.5 text-caption text-white">
          {durationLabel(props.durationSeconds)}
        </span>
      )}
    </div>
  );
}

/** `pending` (the file has never been stored) shows a visible caption beside
 *  its kind icon; a runtime load failure keeps the icon alone, named only for
 *  assistive tech, since that state is transient rather than an ongoing wait. */
function Placeholder(props: {
  alt: string;
  kind: 'image' | 'video';
  pending: boolean;
  className?: string;
  children?: ReactNode;
}): ReactNode {
  const Icon = props.kind === 'video' ? Film : ImageIcon;
  return (
    <div
      className={`flex flex-col items-center justify-center gap-1.5 bg-secondary text-muted-foreground ${props.className ?? ''}`}
    >
      <Icon aria-hidden className="size-6" />
      {props.pending ? (
        <span className="text-caption px-2 text-center">{media('placeholder.pending')}</span>
      ) : (
        <span className="sr-only">{props.alt}</span>
      )}
      {props.children}
    </div>
  );
}

/** One item's frame: a card is a link over its own content, so its video never
 *  carries `controls` — a poster stands in (falling back to a silent, inline
 *  video) with a centred play badge and the clip's length; a detail page's
 *  video plays with full controls instead. Loading shows a skeleton; a source
 *  that fails to load (an expired signed URL) swaps to the tinted placeholder
 *  a post with no stored media already draws, never a broken image. */
export function MediaView(props: {
  item: MediaItem;
  variant: 'card' | 'detail';
  aspect?: '4/5' | '9/16';
  alt: string;
  className?: string;
  /** Laid over the frame, inside its `relative` box — the multi-item count badge. */
  overlay?: ReactNode;
}): ReactNode {
  const { item, variant, alt } = props;
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // A card always boxes to an aspect (4:5 default, 9:16 for a reel or a story); a detail page
  // boxes only when told to (a story's own page keeps its 9:16 frame) and otherwise just bounds
  // itself and lets the media's own shape show, letterboxed by `object-contain`.
  const boxed = variant === 'card' || props.aspect !== undefined;
  const aspectClass = props.aspect === '9/16' ? 'aspect-story' : 'aspect-portrait';
  const bg = variant === 'card' ? 'bg-secondary' : 'bg-black';
  const frameClass = `${
    boxed
      ? `relative flex items-center justify-center overflow-hidden ${bg} ${aspectClass}`
      : `relative flex max-h-media items-center justify-center overflow-hidden ${bg}`
  } ${props.className ?? ''}`;
  const fit = variant === 'card' ? 'size-full object-cover' : 'size-full object-contain';

  if (item.source_url === null || failed) {
    return (
      <Placeholder
        alt={alt}
        kind={item.media_kind}
        pending={item.pending && !failed}
        className={frameClass}
      >
        {props.overlay}
      </Placeholder>
    );
  }

  return (
    <div className={frameClass}>
      {!loaded && <Skeleton aria-hidden className="absolute inset-0 rounded-none" />}
      {item.media_kind === 'video' ? (
        variant === 'detail' ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption -- the provider sends no caption track for a collected clip
          <video
            aria-label={alt}
            src={item.source_url}
            poster={item.poster_url ?? undefined}
            controls
            playsInline
            preload="metadata"
            className={fit}
            onLoadedData={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        ) : item.poster_url !== null ? (
          <>
            <img
              alt={alt}
              src={item.poster_url}
              loading="lazy"
              decoding="async"
              className={fit}
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
            />
            <PlayBadge durationSeconds={item.duration_seconds} />
          </>
        ) : (
          <>
            <video
              aria-label={alt}
              src={item.source_url}
              muted
              playsInline
              preload="metadata"
              className={fit}
              onLoadedData={() => setLoaded(true)}
              onError={() => setFailed(true)}
            />
            <PlayBadge durationSeconds={item.duration_seconds} />
          </>
        )
      ) : (
        <img
          alt={alt}
          src={item.source_url}
          loading="lazy"
          decoding="async"
          className={fit}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
      {props.overlay}
    </div>
  );
}

/** A carousel badge (stack icon + count) laid over a card's first item —
 *  a card stays a single, non-interactive frame; the carousel itself is
 *  `MediaGallery`, for a detail page. */
export function MediaCountBadge(props: { count: number }): ReactNode {
  if (props.count <= 1) return null;
  return (
    <span
      aria-hidden
      className="absolute top-1.5 right-1.5 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-caption text-white"
    >
      <Layers className="size-3" />
      {props.count}
    </span>
  );
}

/** A post's media as a card draws it: the first item, plus a count badge once
 *  there is more than one. `null` for a post with no stored media at all,
 *  same as before this shape existed. */
export function MediaCard(props: {
  media: MediaItem[];
  aspect?: '4/5' | '9/16';
  alt: string;
  className?: string;
}): ReactNode {
  const first = props.media[0];
  if (first === undefined) return null;
  return (
    <MediaView
      item={first}
      variant="card"
      aspect={props.aspect}
      alt={props.alt}
      className={props.className}
      overlay={<MediaCountBadge count={props.media.length} />}
    />
  );
}

const SWIPE_THRESHOLD_PX = 40;

/** A detail page's whole media set: one item renders as `MediaView` alone;
 *  more than one gets prev/next buttons, a swipe gesture and a dot track
 *  reading "n / total" (`ariaLive` announces the position on every change),
 *  each dot itself a stop nothing hides. Children may mix image and video —
 *  a carousel is simply `media.length > 1`. */
export function MediaGallery(props: {
  media: MediaItem[];
  aspect?: '4/5' | '9/16';
  alt: string;
}): ReactNode {
  const { media: items, alt } = props;
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const first = items[0];
  if (first === undefined) return null;
  if (items.length === 1) {
    return <MediaView item={first} variant="detail" aspect={props.aspect} alt={alt} />;
  }
  const clamped = Math.min(index, items.length - 1);
  const current = items[clamped] ?? first;
  const go = (delta: number): void => {
    setIndex((value) => (value + delta + items.length) % items.length);
  };

  return (
    <div
      className="relative"
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        const start = touchStartX.current;
        const end = event.changedTouches[0]?.clientX;
        touchStartX.current = null;
        if (start === null || end === undefined) return;
        const delta = end - start;
        if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
        go(delta > 0 ? -1 : 1);
      }}
    >
      <MediaView item={current} variant="detail" aspect={props.aspect} alt={alt} />
      <button
        type="button"
        onClick={() => go(-1)}
        aria-label={media('gallery.prev')}
        className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
      >
        <ChevronLeft aria-hidden className="size-4" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label={media('gallery.next')}
        className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
      >
        <ChevronRight aria-hidden className="size-4" />
      </button>
      <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
        {items.map((item, dotIndex) => (
          <span
            key={item.ordinal}
            aria-hidden
            className={`size-1.5 rounded-full ${dotIndex === clamped ? 'bg-white' : 'bg-white/50'}`}
          />
        ))}
      </div>
      <span role="status" aria-live="polite" className="sr-only">
        {media('gallery.position', { n: clamped + 1, total: items.length })}
      </span>
    </div>
  );
}
